import type { CaseListFilters } from "@/api/endpoints/build-case-report-metadata";
import { CASE_STATUSES, type CaseStatus } from "@/domain/case/case";

/**
 * 一覧の絞り込み状態を URL に置く。共有・リロード・戻る操作で同じ結果を再現するため、
 * 状態の正本は URL（コンポーネント state ではない）。
 *
 * 既定値のキーは書き出さない（URL を短く保ち、既定と明示指定を区別しない）。
 */
export const EMPTY_FILTERS: CaseListFilters = {
  involved: false,
  keyword: "",
  statuses: [],
  processModelId: null,
  submittedFrom: null,
  submittedTo: null,
};

export type ParsedFilters = {
  readonly filters: CaseListFilters;
  readonly page: number;
};

export function parseFiltersFromSearch(params: URLSearchParams): ParsedFilters {
  return {
    filters: {
      // 既定は自分の起票。未知の値も既定に倒す（URL を手で編集されても壊れない）
      involved: params.get("scope") === "involved",
      keyword: params.get("keyword") ?? "",
      statuses: parseStatuses(params.get("status")),
      processModelId: emptyToNull(params.get("model")),
      submittedFrom: emptyToNull(params.get("from")),
      submittedTo: emptyToNull(params.get("to")),
    },
    page: parsePage(params.get("page")),
  };
}

export function filtersToSearchParams(
  filters: CaseListFilters,
  page: number,
): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.involved === true) params.set("scope", "involved");

  // 前後の空白は落として書き出す（URL に残すと検索条件にもそのまま乗る）
  const keyword = filters.keyword.trim();
  if (keyword !== "") params.set("keyword", keyword);
  if (filters.statuses.length > 0) {
    params.set("status", filters.statuses.join(","));
  }
  if (filters.processModelId !== null) {
    params.set("model", filters.processModelId);
  }
  if (filters.submittedFrom !== null) params.set("from", filters.submittedFrom);
  if (filters.submittedTo !== null) params.set("to", filters.submittedTo);
  if (page > 1) params.set("page", String(page));

  return params;
}

/** 未知のステータスは捨てる（URL は利用者が編集できるため信用しない）。 */
function parseStatuses(value: string | null): readonly CaseStatus[] {
  if (value === null || value === "") return [];

  return value
    .split(",")
    .map((part) => part.trim())
    .filter((part): part is CaseStatus =>
      (CASE_STATUSES as readonly string[]).includes(part),
    );
}

function parsePage(value: string | null): number {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : 1;
}

function emptyToNull(value: string | null): string | null {
  return value === null || value.trim() === "" ? null : value;
}
